import type { FormContextLeaf } from "./form-context-merge-tags.utils";
import type { MergeTagMenuEntry } from "./signature-merge-tags.utils";

/*
 * ENG-2498: what Anvil fills on a patient form, as tags on a Digital Form placed on Patient Forms.
 * Their values exist only while such a form is filled from Patient chart > Forms with
 * `digital-form-tags-patient-forms` on (`patientForm` in `additionalMergeTagsValues`).
 *
 * {{Patient.HearingAids.Left.Condition}}, {{Patient.HearingAids.RepairWarrantyExp}},
 * {{Patient.Audiogram.Left.Air.1_5K}}, {{Patient.Audiogram.Left.Air.1_5K.Masked}}.
 */

type Segment = { tag: string; key: string; label: string };

// The report's frequencies (HZ_VALUES) less the three Anvil does not fill: 125, 12K and 16K.
export const PATIENT_FORM_AUDIOGRAM_FREQUENCIES = [
  { tag: "250", key: "250", label: "250", hz: 250 },
  { tag: "500", key: "500", label: "500", hz: 500 },
  { tag: "750", key: "750", label: "750", hz: 750 },
  { tag: "1K", key: "1K", label: "1K", hz: 1000 },
  { tag: "1_5K", key: "1_5K", label: "1.5K", hz: 1500 },
  { tag: "2K", key: "2K", label: "2K", hz: 2000 },
  { tag: "3K", key: "3K", label: "3K", hz: 3000 },
  { tag: "4K", key: "4K", label: "4K", hz: 4000 },
  { tag: "6K", key: "6K", label: "6K", hz: 6000 },
  { tag: "8K", key: "8K", label: "8K", hz: 8000 }
] as const;

const EARS: Segment[] = [
  { tag: "Left", key: "left", label: "Left" },
  { tag: "Right", key: "right", label: "Right" }
];
const CONDUCTIONS: Segment[] = [
  { tag: "Air", key: "air", label: "Air" },
  { tag: "Bone", key: "bone", label: "Bone" }
];
const DEVICE_FIELDS: Segment[] = [
  { tag: "Condition", key: "condition", label: "Condition" },
  { tag: "Price", key: "price", label: "Price" },
  { tag: "UnitPrice", key: "unitPrice", label: "Unit Price" },
  { tag: "PurchaseDate", key: "purchaseDate", label: "Purchase Date" },
  { tag: "DisplayName", key: "displayName", label: "Display Name" },
  { tag: "Battery", key: "battery", label: "Battery" }
];
const EITHER_EAR_FIELDS: Segment[] = [
  {
    tag: "RepairWarrantyExp",
    key: "repairWarrantyExp",
    label: "Repair Warranty Expiry"
  },
  {
    tag: "LandWarrantyExp",
    key: "landWarrantyExp",
    label: "L&D Warranty Expiry"
  }
];

const ROOT = { tag: "Patient", key: "patientForm", label: "Patient" };
const HEARING_AIDS = { tag: "HearingAids", key: "", label: "Hearing Aids" };
const AUDIOGRAM = { tag: "Audiogram", key: "audiogram", label: "Audiogram" };
const VALUE = { tag: "", key: "value", label: "" };
const MASKED = { tag: "Masked", key: "masked", label: "Masked" };

// A segment with an empty `tag`, `key` or `label` adds nothing to that part: the hearing aids sit at the
// top of `patientForm`, and a threshold's tag and title end at its frequency while its value sits under it.
const join = (segments: Segment[], part: keyof Segment) =>
  segments.map((segment) => segment[part]).filter(Boolean);

const toLeaf = (segments: Segment[]): FormContextLeaf => ({
  value: join(segments, "tag").join("."),
  title: join(segments, "label").join(" - "),
  path: join(segments, "key")
});

const toItem = ({ title, value }: FormContextLeaf): MergeTagMenuEntry => ({
  title,
  value
});

const deviceLeaves = (ear: Segment) =>
  DEVICE_FIELDS.map((field) => toLeaf([ROOT, HEARING_AIDS, ear, field]));
const eitherEarLeaves = EITHER_EAR_FIELDS.map((field) =>
  toLeaf([ROOT, HEARING_AIDS, field])
);
const conductionLeaves = (ear: Segment, conduction: Segment) =>
  PATIENT_FORM_AUDIOGRAM_FREQUENCIES.flatMap((frequency) => {
    const threshold = [ROOT, AUDIOGRAM, ear, conduction, frequency];
    return [toLeaf([...threshold, VALUE]), toLeaf([...threshold, MASKED])];
  });

export const PATIENT_FORM_LEAVES: readonly FormContextLeaf[] = [
  ...EARS.flatMap(deviceLeaves),
  ...eitherEarLeaves,
  ...EARS.flatMap((ear) =>
    CONDUCTIONS.flatMap((conduction) => conductionLeaves(ear, conduction))
  )
];

export const PATIENT_FORM_MENU_TITLE = "Patient Form";

export const PATIENT_FORM_MENU_ITEMS: MergeTagMenuEntry[] = [
  {
    title: HEARING_AIDS.label,
    menu: [
      ...EARS.map((ear) => ({
        title: ear.label,
        menu: deviceLeaves(ear).map(toItem)
      })),
      ...eitherEarLeaves.map(toItem)
    ]
  },
  {
    title: AUDIOGRAM.label,
    menu: EARS.map((ear) => ({
      title: ear.label,
      menu: CONDUCTIONS.map((conduction) => ({
        title: conduction.label,
        menu: conductionLeaves(ear, conduction).map(toItem)
      }))
    }))
  }
];
